import { useState, useEffect, useCallback, memo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import api, { BACKEND_URL } from '@/lib/api';
import { Save, Upload, Trash2 } from 'lucide-react';

// Field component defined OUTSIDE to prevent re-creation on each render
const CMSField = memo(({ label, settingKey, multiline, placeholder, value, onChange }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    {multiline ? (
      <Textarea 
        value={value} 
        onChange={e => onChange(settingKey, e.target.value)} 
        rows={3} 
        className="mt-1 rounded-sm" 
        placeholder={placeholder} 
        data-testid={`cms-${settingKey}`} 
      />
    ) : (
      <Input 
        value={value} 
        onChange={e => onChange(settingKey, e.target.value)} 
        className="mt-1 rounded-sm" 
        placeholder={placeholder} 
        data-testid={`cms-${settingKey}`} 
      />
    )}
  </div>
));

CMSField.displayName = 'CMSField';

export default function AdminCMS() {
  const [settings, setSettings] = useState([]);
  const [logoUploading, setLogoUploading] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);

  useEffect(() => {
    api.get('/site-settings/all').then(r => {
      setSettings(r.data);
    }).catch(() => {});
  }, []);

  const getValue = useCallback((key) => settings.find(s => s.key === key)?.value || '', [settings]);
  
  const setValue = useCallback((key, value) => {
    setSettings(prev => prev.map(s => s.key === key ? { ...s, value } : s));
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/media/upload', fd);
      setValue('logo_url', res.data.url);
      toast.success('Logo hochgeladen');
    } catch { toast.error('Upload fehlgeschlagen'); }
    finally { setLogoUploading(false); }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeroUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/media/upload', fd);
      setValue('hero_image', res.data.url);
      toast.success('Hero-Bild hochgeladen');
    } catch { toast.error('Upload fehlgeschlagen'); }
    finally { setHeroUploading(false); }
  };

  const handleSave = async () => {
    try {
      const updates = settings.map(s => ({ key: s.key, value: s.value }));
      await api.put('/site-settings', updates);
      toast.success('Alle Einstellungen gespeichert');
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.statusText || e.message || 'Unbekannter Fehler';
      toast.error(`Fehler beim Speichern: ${msg}`);
    }
  };

  // Helper to get image URL with backend prefix if needed
  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  return (
    <div data-testid="admin-cms">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0E518D]">CMS - Inhalte</h1>
        <Button onClick={handleSave} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide text-xs" data-testid="cms-save">
          <Save className="h-4 w-4 mr-1" /> Alles speichern
        </Button>
      </div>

      <div className="space-y-6">
        {/* Logo */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Logo</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-slate-400">Laden Sie hier das Shop-Logo hoch. Es wird automatisch im Header angezeigt.</p>
            <div className="flex items-center gap-4">
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={logoUploading} data-testid="cms-logo-upload" />
                <Button asChild variant="outline" className="rounded-sm text-xs" disabled={logoUploading}>
                  <span><Upload className="h-3.5 w-3.5 mr-1" /> {logoUploading ? 'Hochladen...' : 'Logo hochladen'}</span>
                </Button>
              </label>
              {getValue('logo_url') && (
                <div className="flex items-center gap-3">
                  <img src={getImageUrl(getValue('logo_url'))} alt="Logo" className="h-12 object-contain bg-slate-50 p-1 rounded-sm" />
                  <Button variant="ghost" size="sm" onClick={() => setValue('logo_url', '')} className="text-red-400 hover:text-red-600 text-xs" data-testid="cms-remove-logo"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">oder URL direkt eingeben</Label>
              <Input value={getValue('logo_url')} onChange={e => setValue('logo_url', e.target.value)} className="mt-1 rounded-sm" placeholder="/api/uploads/logo.png" data-testid="cms-logo-url" />
            </div>
          </CardContent>
        </Card>

        {/* Header / Navigation */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Header / Navigation</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <CMSField label="Header Untertitel (neben Logo)" settingKey="header_subtitle" value={getValue('header_subtitle')} onChange={setValue} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <CMSField label="Nav: Link 1" settingKey="nav_home" value={getValue('nav_home')} onChange={setValue} />
              <CMSField label="Nav: Link 2" settingKey="nav_products" value={getValue('nav_products')} onChange={setValue} />
              <CMSField label="Nav: Link 3" settingKey="nav_faq" value={getValue('nav_faq')} onChange={setValue} />
            </div>
          </CardContent>
        </Card>

        {/* Hero */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Hero-Bereich (Startseite)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <CMSField label="Badge-Text" settingKey="hero_badge" value={getValue('hero_badge')} onChange={setValue} />
            
            {/* Hero Info Box */}
            <div className="pt-2 pb-2 border-t border-b border-slate-100 bg-amber-50/30 -mx-6 px-6">
              <p className="text-xs font-medium text-amber-700 mb-3">Info-Box (optional, erscheint zwischen Badge und Überschrift)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <CMSField label="Info-Box Titel" settingKey="hero_infobox_title" placeholder="z.B. Schnell online" value={getValue('hero_infobox_title')} onChange={setValue} />
                <CMSField label="Info-Box Text" settingKey="hero_infobox_text" placeholder="z.B. Einfach Bilder auswählen und direkt hochladen" value={getValue('hero_infobox_text')} onChange={setValue} />
              </div>
              <p className="text-xs text-slate-400 mt-2">Lassen Sie beide Felder leer, um die Info-Box auszublenden.</p>
            </div>
            
            <CMSField label="Überschrift" settingKey="hero_title" value={getValue('hero_title')} onChange={setValue} />
            <CMSField label="Untertitel" settingKey="hero_subtitle" multiline value={getValue('hero_subtitle')} onChange={setValue} />
            <CMSField label="Button-Text" settingKey="hero_cta_text" value={getValue('hero_cta_text')} onChange={setValue} />
            
            {/* Hero Image Upload */}
            <div className="pt-2 border-t border-slate-100">
              <Label className="text-sm font-medium">Hero-Bild</Label>
              <p className="text-xs text-slate-400 mb-3">Das große Bild rechts im Hero-Bereich der Startseite.</p>
              <div className="flex items-start gap-4">
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={handleHeroUpload} className="hidden" disabled={heroUploading} data-testid="cms-hero-upload" />
                  <Button asChild variant="outline" className="rounded-sm text-xs" disabled={heroUploading}>
                    <span><Upload className="h-3.5 w-3.5 mr-1" /> {heroUploading ? 'Hochladen...' : 'Hero-Bild hochladen'}</span>
                  </Button>
                </label>
                {getValue('hero_image') && (
                  <div className="flex items-center gap-3">
                    <img src={getImageUrl(getValue('hero_image'))} alt="Hero" className="h-20 w-32 object-cover bg-slate-50 rounded-sm" />
                    <Button variant="ghost" size="sm" onClick={() => setValue('hero_image', '')} className="text-red-400 hover:text-red-600 text-xs" data-testid="cms-remove-hero"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
              </div>
              <div className="mt-3">
                <Label className="text-xs">oder URL direkt eingeben</Label>
                <Input value={getValue('hero_image')} onChange={e => setValue('hero_image', e.target.value)} className="mt-1 rounded-sm" placeholder="/api/uploads/hero.jpg oder https://..." data-testid="cms-hero-image-url" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Trust / USP */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Vorteile / USP-Banner</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-slate-400">Die drei Vorteile im blauen Banner unter dem Hero.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <CMSField label="Vorteil 1" settingKey="trust_1" value={getValue('trust_1')} onChange={setValue} />
              <CMSField label="Vorteil 2" settingKey="trust_2" value={getValue('trust_2')} onChange={setValue} />
              <CMSField label="Vorteil 3" settingKey="trust_3" value={getValue('trust_3')} onChange={setValue} />
            </div>
          </CardContent>
        </Card>

        {/* Products Section */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Produktbereich</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <CMSField label="Abschnitts-Überschrift" settingKey="products_title" value={getValue('products_title')} onChange={setValue} />
            <CMSField label="Abschnitts-Untertitel" settingKey="products_subtitle" multiline value={getValue('products_subtitle')} onChange={setValue} />
            <CMSField label="Suchfeld Placeholder" settingKey="products_search_placeholder" value={getValue('products_search_placeholder')} onChange={setValue} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CMSField label="Leer-Meldung (keine Produkte)" settingKey="products_empty" value={getValue('products_empty')} onChange={setValue} />
              <CMSField label="Leer-Meldung Unterzeile" settingKey="products_empty_sub" value={getValue('products_empty_sub')} onChange={setValue} />
              <CMSField label="Keine Treffer (Suche)" settingKey="products_no_results" value={getValue('products_no_results')} onChange={setValue} />
              <CMSField label="Keine Treffer Unterzeile" settingKey="products_no_results_sub" value={getValue('products_no_results_sub')} onChange={setValue} />
            </div>
          </CardContent>
        </Card>

        {/* Intro */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Einleitung / Freitext</CardTitle></CardHeader>
          <CardContent>
            <CMSField label="Einleitungstext (HTML möglich)" settingKey="intro_text" multiline placeholder="HTML-Inhalt..." value={getValue('intro_text')} onChange={setValue} />
          </CardContent>
        </Card>

        {/* Steps */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">So funktioniert es</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <CMSField label="Abschnitts-Überschrift" settingKey="steps_title" value={getValue('steps_title')} onChange={setValue} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CMSField label="Schritt 1 Titel" settingKey="step_1_title" value={getValue('step_1_title')} onChange={setValue} />
              <CMSField label="Schritt 1 Beschreibung" settingKey="step_1_desc" value={getValue('step_1_desc')} onChange={setValue} />
              <CMSField label="Schritt 2 Titel" settingKey="step_2_title" value={getValue('step_2_title')} onChange={setValue} />
              <CMSField label="Schritt 2 Beschreibung" settingKey="step_2_desc" value={getValue('step_2_desc')} onChange={setValue} />
              <CMSField label="Schritt 3 Titel" settingKey="step_3_title" value={getValue('step_3_title')} onChange={setValue} />
              <CMSField label="Schritt 3 Beschreibung" settingKey="step_3_desc" value={getValue('step_3_desc')} onChange={setValue} />
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">FAQ (Startseite)</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <CMSField label="FAQ Überschrift" settingKey="faq_title" placeholder="Häufig gestellte Fragen" value={getValue('faq_title')} onChange={setValue} />
            {[1,2,3,4].map(i => (
              <div key={`faq-${i}`} className="bg-slate-50 p-4 rounded-sm space-y-3" data-testid={`cms-faq-${i}`}>
                <p className="text-xs font-semibold text-slate-500">FAQ {i}</p>
                <CMSField label="Frage" settingKey={`faq_${i}_question`} value={getValue(`faq_${i}_question`)} onChange={setValue} />
                <CMSField label="Antwort" settingKey={`faq_${i}_answer`} multiline value={getValue(`faq_${i}_answer`)} onChange={setValue} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">CTA-Bereich (Call to Action)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <CMSField label="Überschrift" settingKey="cta_title" value={getValue('cta_title')} onChange={setValue} />
            <CMSField label="Beschreibungstext" settingKey="cta_text" multiline value={getValue('cta_text')} onChange={setValue} />
            <CMSField label="Button-Text" settingKey="cta_button" value={getValue('cta_button')} onChange={setValue} />
          </CardContent>
        </Card>

        {/* Checkout / Form */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Bestellformular / Checkout</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CMSField label="Seiten-Überschrift" settingKey="checkout_title" value={getValue('checkout_title')} onChange={setValue} />
              <CMSField label="Zusammenfassung Titel" settingKey="checkout_summary_title" value={getValue('checkout_summary_title')} onChange={setValue} />
              <CMSField label="Formular Titel" settingKey="checkout_form_title" value={getValue('checkout_form_title')} onChange={setValue} />
              <CMSField label="Zurück-Button Text" settingKey="checkout_back" value={getValue('checkout_back')} onChange={setValue} />
              <CMSField label="Absenden-Button Text" settingKey="checkout_submit" value={getValue('checkout_submit')} onChange={setValue} />
              <CMSField label="Zahlungshinweis" settingKey="checkout_payment_hint" value={getValue('checkout_payment_hint')} onChange={setValue} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CMSField label="Warenkorb leer Titel" settingKey="checkout_empty_title" value={getValue('checkout_empty_title')} onChange={setValue} />
              <CMSField label="Warenkorb leer Text" settingKey="checkout_empty_text" value={getValue('checkout_empty_text')} onChange={setValue} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CMSField label="Lieferart: Abholung" settingKey="delivery_pickup" value={getValue('delivery_pickup')} onChange={setValue} />
              <CMSField label="Lieferart: Wohnung" settingKey="delivery_apartment" value={getValue('delivery_apartment')} onChange={setValue} />
            </div>
          </CardContent>
        </Card>

        {/* Confirmation */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Bestellbestätigung</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <CMSField label="Danke-Überschrift" settingKey="confirm_title" value={getValue('confirm_title')} onChange={setValue} />
            <CMSField label="Bestätigungstext" settingKey="confirm_text" value={getValue('confirm_text')} onChange={setValue} />
            <CMSField label="Zahlungshinweis" settingKey="confirm_payment_hint" value={getValue('confirm_payment_hint')} onChange={setValue} />
          </CardContent>
        </Card>

        {/* Cart */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Warenkorb</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CMSField label="Warenkorb Titel" settingKey="cart_title" value={getValue('cart_title')} onChange={setValue} />
              <CMSField label="Leer-Meldung" settingKey="cart_empty" value={getValue('cart_empty')} onChange={setValue} />
              <CMSField label="Leer-Meldung Unterzeile" settingKey="cart_empty_sub" value={getValue('cart_empty_sub')} onChange={setValue} />
              <CMSField label="Zur Bestellung Button" settingKey="cart_checkout_button" value={getValue('cart_checkout_button')} onChange={setValue} />
              <CMSField label="Warenkorb leeren Button" settingKey="cart_clear_button" value={getValue('cart_clear_button')} onChange={setValue} />
            </div>
          </CardContent>
        </Card>

        {/* System Messages */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Systemmeldungen</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CMSField label="Bestellung erfolgreich" settingKey="msg_order_success" value={getValue('msg_order_success')} onChange={setValue} />
              <CMSField label="Warenkorb leer (Toast)" settingKey="msg_cart_empty" value={getValue('msg_cart_empty')} onChange={setValue} />
            </div>
            <CMSField label="Pflichtfelder fehlen" settingKey="msg_fill_required" value={getValue('msg_fill_required')} onChange={setValue} />
          </CardContent>
        </Card>

        {/* Footer */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Footer</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CMSField label="Titel" settingKey="footer_title" value={getValue('footer_title')} onChange={setValue} />
              <CMSField label="Copyright-Zeile" settingKey="footer_text" value={getValue('footer_text')} onChange={setValue} />
            </div>
            <CMSField label="Beschreibung" settingKey="footer_description" multiline value={getValue('footer_description')} onChange={setValue} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CMSField label="Kontakt Überschrift" settingKey="footer_contact_title" value={getValue('footer_contact_title')} onChange={setValue} />
              <CMSField label="Kontakt E-Mail" settingKey="footer_contact_email" value={getValue('footer_contact_email')} onChange={setValue} />
              <CMSField label="Hinweis Überschrift" settingKey="footer_hint_title" value={getValue('footer_hint_title')} onChange={setValue} />
              <CMSField label="Hinweis Text" settingKey="footer_hint_text" value={getValue('footer_hint_text')} onChange={setValue} />
            </div>
          </CardContent>
        </Card>

        {/* Hinweistext */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Hinweistext / Freitext</CardTitle></CardHeader>
          <CardContent>
            <CMSField label="Hinweistext (HTML möglich)" settingKey="info_text" multiline placeholder="HTML-Inhalt..." value={getValue('info_text')} onChange={setValue} />
          </CardContent>
        </Card>

        {/* Beliebte Produkte */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader><CardTitle className="text-lg text-[#0E518D]">Beliebte Produkte</CardTitle></CardHeader>
          <CardContent>
            <Label className="text-sm">Schwellenwert (Mindestbestellungen)</Label>
            <Input type="number" value={getValue('popular_threshold')} onChange={e => setValue('popular_threshold', e.target.value)} className="mt-1 rounded-sm w-40" data-testid="cms-popular-threshold" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
